import React, { useEffect, useState } from "react";
import ReportsModal from "../../../core/modals/reports_modal";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../../../core/common/crmComponents/PageHeader";
import LoaderComponent from "../../../core/common/crmComponents/LoaderComponent";
import PaginationControls from "../../../core/common/pagination";
import CompanyFilter from "../../../core/common/crmComponents/CompanyFilter";
import Filter from "../common-components/Filter";
import api from "../../../utils/api";
import { useDealsData } from "../../crm/deals/hooks/useDealsData";
import DealsByYearBarChart from "./components/DealsByYearBarChart";
import Deals from "../../crm/deals/containers/Deals";
import DealsBySourceChart from "./components/DealsBySourceChart";
import DealList from "./components/dealList";
import { useDispatch, useSelector } from "react-redux";
import { MODULES } from "../../../core/data/constants/moduleConstants";
import CommonFilter from "../../../core/common/crmComponents/filters/CommonFilter";
import ClearAllFilters from "../../../core/common/crmComponents/filters/ClearAllFilters";
import { generateQueryParams } from "../../../utils/helpers/generateQueryParams";
import ReloadButton from "../../../core/common/crmComponents/ReloadButton";
import queryClient from "../../../config/queryClient";
import { setReloading } from "../../../core/data/redux/moduleDataSlice";
import { useFunctionalityAccess } from "../../../core/common/wrapper/HasFunctionalityAccess";
import { toast } from "react-toastify";

const getDealReportDateKeys = (dateFilterOn = "createdTime") => {
  if (dateFilterOn === "paymentDate") {
    return {
      gte: "verificationRecord[paymentDate][gte]",
      lte: "verificationRecord[paymentDate][lte]",
    };
  }

  return {
    gte: "createdTime[gte]",
    lte: "createdTime[lte]",
  };
};

const DealReport = () => {
  const user = JSON.parse(localStorage.getItem("user") || []);
  const [page, setPage] = useState(1); // Manage current page state
  const [limit, setLimit] = useState(25);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const reloading = useSelector((state) => state.moduleData.reloading);

  const [layout, setLayout] = useState("list");
  const { companyNameArr } = useSelector((state) => state.auth);

  const hasDownloadAccess = useFunctionalityAccess("downloadReport");
  const [isDownloading, setIsDownloading] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(() => {
    const companyIdFromParams = searchParams.get("companyid");

    return companyIdFromParams
      ? companyIdFromParams
      : user?.ofCompany
        ? user?.ofCompany?._id
        : companyNameArr.length > 0
          ? companyNameArr[0].value
          : null;
  });

  const { filters } = useSelector((state) => state.filter);
  const dealsFilters = filters.deals || [];

  const { queryParams, params } = generateQueryParams(dealsFilters);

  const { data, isLoading, isError, error, isFetching } = useDealsData({
    page,
    limit,
    ofCompany: selectedCompany,
    queryString: params.toString(),
    queryParams: queryParams.toString(),
  });

  const [reportDataFilter, setReportDataFilter] = useState({
    format: "excel",
    fromDate: "",
    toDate: "",
    dateFilterOn: "createdTime",
    filters: {
      ofCompany: selectedCompany,
      queryString: params.toString(),
      queryParams: queryParams.toString(),
    },
  });

  const dealData = data?.data?.data;

  const totalCount = data?.data?.totalResult;

  const totalPages = Math.ceil(totalCount / limit);

  const handlePageChange = (newPage) => {
    setPage(newPage); // Update page state to trigger refetch
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset page to 1 whenever limit changes
  };

  useEffect(() => {
    setReportDataFilter((prev) => {
      const existingParams = new URLSearchParams(
        prev.filters?.queryParams || "",
      );
      const newParams = new URLSearchParams(queryParams.toString());

      // Preserve any additional keys (e.g., format, date range) from existingParams
      for (const [key, value] of existingParams.entries()) {
        if (!newParams.has(key)) {
          newParams.set(key, value);
        }
      }

      return {
        ...prev,
        filters: {
          ...prev.filters,
          ofCompany: selectedCompany,
          queryString: params.toString(),
          queryParams: newParams.toString(),
        },
      };
    });
  }, [selectedCompany, dealsFilters]);
  // console.log(filters, reportDataFilter);

  const handleDownload = async () => {
    const { fromDate, toDate, format, filters, dateFilterOn } =
      reportDataFilter;
    const params = new URLSearchParams(filters?.queryParams || "");
    const { gte, lte } = getDealReportDateKeys(dateFilterOn);
    const queryFromDate = params.get(gte);
    const queryToDate = params.get(lte);
    const effectiveFromDate = queryFromDate || fromDate;
    const effectiveToDate = queryToDate || toDate;

    if (!queryFromDate || !queryToDate) {
      toast.error(
        `Please select ${
          dateFilterOn === "paymentDate" ? "payment date" : "created time"
        } from and to dates before downloading.`,
      );
      return;
    }

    const from = new Date(effectiveFromDate);
    const to = new Date(effectiveToDate);
    const diffInMonths =
      to.getMonth() -
      from.getMonth() +
      12 * (to.getFullYear() - from.getFullYear());
    console.log({ from, to, diffInMonths });

    if (diffInMonths > 3 || from > to) {
      toast.error(
        `You can only download reports for a maximum of 3 months based on ${
          dateFilterOn === "paymentDate" ? "payment date" : "created time"
        }.`,
      );
      return;
    }

    setIsDownloading(true); // Start loading

    try {
      const { queryString, queryParams, ...restParams } = filters;
      const response = await api.get(
        `/file-system/download-deal-report?${queryString}&${queryParams}&populate=ofCompany,assignedTo,lastUpdationBy&selectPopulate=name`,
        {
          params: {
            format,
            ...restParams,
            fields: "-timeline",
          },
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `deals-report.${format === "excel" ? "xlsx" : "csv"}`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error(error?.response?.data?.msg || "Failed to download report.");
    } finally {
      setIsDownloading(false); // End loading
    }
  };

  useEffect(() => {
    if (dealsFilters?.length > 0) {
      setPage(1);
    }
  }, [dealsFilters]);

  useEffect(() => {
    if (reloading) {
      queryClient.invalidateQueries(["deals"]);
      dispatch(setReloading(false));
    }
  }, [reloading, dispatch]);

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content custom-content-padding">
          <div className="row">
            <div className="col-md-12">
              <PageHeader
                pageName="Deals Report"
                totalCount={totalCount || 0}
              />
              <div className="card">
                <div className="card-header py-2">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                    {/* Left Section */}
                    <div className="d-flex flex-wrap gap-3">
                      <ReloadButton moduleName={MODULES.DEALS} />

                      {/* Company Filter */}
                      <CompanyFilter
                        selectedCompany={selectedCompany}
                        setSelectedCompany={setSelectedCompany}
                        user={user}
                      />
                    </div>

                    {/* Right Section */}
                    <div className="d-flex align-items-center gap-3">
                      <div className="gap-1 d-flex align-items-center flex-wrap">
                        <CommonFilter module={MODULES.DEALS} />
                        <ClearAllFilters module={MODULES.DEALS} />
                      </div>

                      {hasDownloadAccess && (
                        <button
                          className="btn btn-primary custom-btn-padding"
                          data-bs-toggle="modal"
                          data-bs-target="#download_report"
                          disabled={isDownloading}
                        >
                          {isDownloading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm fs-5 me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <i className="ti ti-file-download fs-5 me-2" />
                              Download Report
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-body py-2">
                  {/* <div className="row"> */}
                  {/* <DealsByYearBarChart selectedCompany={selectedCompany} /> */}
                  {/* <DealsBySourceChart
                      user={user}
                      selectedCompany={selectedCompany}
                    /> */}
                  {/* </div> */}

                  <PaginationControls
                    totalPages={totalPages}
                    totalCount={totalCount}
                    currentPageDataCount={Math.min(dealData?.length, limit)}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    moduleName={MODULES.DEALS}
                  />

                  {isLoading ? (
                    <LoaderComponent />
                  ) : isError ? (
                    <div>Error: {error.message}</div>
                  ) : layout === "list" ? (
                    <DealList dealsData={dealData} />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* /Page Wrapper */}
      <ReportsModal
        handleDownload={handleDownload}
        reportDataFilter={reportDataFilter}
        setReportDataFilter={setReportDataFilter}
        module={MODULES.DEALS}
      />
    </div>
  );
};

export default DealReport;
